import { getSourceOptionManifest, listSourceOptions } from '../api';
import { useQuery } from '@tanstack/react-query';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import type { AvailableSource } from '../types';
import { PlanFeature, useIsPlanFeatureEnabled } from '@/features/pipelines/utils';
import { uniq } from 'lodash-es';
import { sourceCategorySorter } from '../utils/source-options';
import { useMemo } from 'react';

export const useListSourceOptions = (workspaceId: number | null, { includeHidden = true } = {}) => {
  return useQuery({
    queryKey: ['sourceOptions', { workspaceId, includeHidden }],
    queryFn: async () => {
      if (workspaceId == null) {
        return Promise.reject();
      }
      let sources = await listSourceOptions(workspaceId);

      if (!includeHidden) {
        sources = sources.filter((source) => !source.hidden);
      }
      return sources;
    },
    enabled: workspaceId != null,
    staleTime: 60 * 1000, // one minute
  });
};

export const useGetSourceOptionManifest = (workspaceId: number | null, optionId: string | null | undefined) => {
  return useQuery({
    queryKey: ['sourceOptions', 'manifest', { id: optionId, workspaceId }],
    queryFn: async () => {
      if (workspaceId == null || optionId == null) {
        return Promise.reject();
      }
      return getSourceOptionManifest(workspaceId, optionId);
    },
    enabled: workspaceId != null && optionId != null,
    staleTime: 60 * 1000, // one minute
  });
};

export function useGetSourceDescriptor(optionId?: string): [AvailableSource | undefined, boolean] {
  const [find, isLoading] = useFindSourceDescriptor();

  if (isLoading) {
    return [undefined, isLoading];
  }

  return [find(optionId), false];
}

/**
 * Returns a function to find source descriptors by optionId. Use this if you need to look for more then one optiondId.
 * @returns [
 *  find,        : function to find source by optionId
 *  isLoading,   : indicates if the source options are still loading
 *  categories   : list of all sources categories (including hidden ones)
 * ]
 */
export function useFindSourceDescriptor(): [(optionId?: string) => AvailableSource | undefined, boolean, string[]] {
  const workspaceId = useWorkspaceIdFromCurrentUrl();
  const sourceOptions = useListSourceOptions(workspaceId);
  const options = sourceOptions.data ?? [];
  const categories = useMemo(() => {
    const unique = uniq(options.map((o) => (o.category.length === 0 ? 'Other' : o.category)));
    const sorted = unique.sort(sourceCategorySorter);
    return sorted;
  }, [options.length]);

  const { isLoading } = sourceOptions;
  if (isLoading) {
    return [() => undefined, true, ['All']];
  }

  return [
    (optionId?: string) => {
      return options.find((s) => s.option_id === optionId);
    },
    false,
    categories,
  ];
}

export function useCanAddSalesforce() {
  const isPlanFeatureEnabled = useIsPlanFeatureEnabled(PlanFeature.CdpPremiumIntegrations).data;

  return isPlanFeatureEnabled === true;
}
